import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAllMovements } from '../../services/CashService';
import type { Movement } from '../../types/@types/cash';
import {
  ArrowUpCircle,
  ArrowDownCircle,
  DollarSign,
  Calendar,
  Search,
  Download,
  Filter,
  CreditCard,
  Banknote,
  Wallet,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { cn } from '../../utils/theme';

export default function CashMovements() {
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'week' | 'month'>('all');

  const { data: movements = [], isLoading, error } = useQuery<Movement[]>({
    queryKey: ['cashMovements'],
    queryFn: getAllMovements,
  });

  const filteredMovements = useMemo(() => {
    let filtered = movements;

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (m) =>
          m.orderId?.toLowerCase().includes(lower) ||
          m.reason?.toLowerCase().includes(lower) ||
          m.method?.toLowerCase().includes(lower)
      );
    }

    const now = new Date();
    if (dateFilter === 'today') {
      filtered = filtered.filter(
        (m) => new Date(m.createdAt).toDateString() === now.toDateString()
      );
    } else if (dateFilter === 'week') {
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter((m) => new Date(m.createdAt) >= oneWeekAgo);
    } else if (dateFilter === 'month') {
      const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter((m) => new Date(m.createdAt) >= oneMonthAgo);
    }

    return filtered;
  }, [movements, searchTerm, dateFilter]);

  const stats = useMemo(() => {
    const income = filteredMovements
      .filter((m) => m.type === 'sale')
      .reduce((acc, m) => acc + m.amount, 0);
    const outcome = filteredMovements
      .filter((m) => m.type === 'withdraw' || m.type === 'expense')
      .reduce((acc, m) => acc + m.amount, 0);
    return { income, outcome, balance: income - outcome };
  }, [filteredMovements]);

  const typeConfig: Record<string, { label: string; icon: React.ElementType; color: string; bg: string; darkBg: string }> = {
    sale: { label: 'Venta', icon: ArrowUpCircle, color: 'text-emerald-600', bg: 'bg-emerald-50', darkBg: 'dark:bg-emerald-900/20' },
    withdraw: { label: 'Retiro', icon: ArrowDownCircle, color: 'text-rose-600', bg: 'bg-rose-50', darkBg: 'dark:bg-rose-900/20' },
    expense: { label: 'Gasto', icon: Wallet, color: 'text-amber-600', bg: 'bg-amber-50', darkBg: 'dark:bg-amber-900/20' },
  };

  const methodIcons: Record<string, React.ElementType> = {
    EFECTIVO: Banknote,
    TARJETA: CreditCard,
    TRANSFERENCIA: DollarSign,
  };

  const dateFilterLabels = {
    all: 'Todos',
    today: 'Hoy',
    week: 'Esta Semana',
    month: 'Este Mes',
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-16">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-muted border-t-primary" />
        <p className="text-sm text-text-muted">Cargando movimientos…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-4 mt-6 rounded-2xl bg-rose-50 p-6 text-center dark:bg-rose-900/20">
        <ArrowDownCircle className="mx-auto mb-2 text-rose-500" size={32} />
        <p className="font-semibold text-rose-700 dark:text-rose-400">Error al cargar los movimientos</p>
        <p className="mt-1 text-sm text-rose-500">Por favor intenta recargar la página.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* ── Header ── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text sm:text-3xl">Movimientos de Caja</h1>
          <p className="mt-0.5 text-sm text-text-muted">Gestión y rastreo de todas las transacciones</p>
        </div>
        <button
          onClick={() => window.open('/cash/movements/export', '_blank')}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-primary-fg shadow-md transition-all duration-200 hover:opacity-90 active:scale-95 sm:w-auto sm:py-2.5"
        >
          <Download size={16} />
          Exportar Reporte
        </button>
      </div>

      {/* ── Stats Cards ── */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        {/* Ingresos */}
        <div className="col-span-1 flex flex-col items-center justify-center gap-1 rounded-2xl border border-border bg-surface px-1 py-3 text-center shadow-sm sm:items-start sm:px-4 sm:text-left">
          <div className="flex items-center gap-1.5">
            <div className="rounded-lg bg-emerald-50 p-1 dark:bg-emerald-900/30">
              <TrendingUp className="text-emerald-600" size={12} />
            </div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-text-muted sm:text-xs">Ingresos</p>
          </div>
          <p className="text-sm font-bold text-emerald-600 sm:text-xl md:text-2xl">
            ${stats.income.toLocaleString()}
          </p>
        </div>

        {/* Egresos */}
        <div className="col-span-1 flex flex-col items-center justify-center gap-1 rounded-2xl border border-border bg-surface px-1 py-3 text-center shadow-sm sm:items-start sm:px-4 sm:text-left">
          <div className="flex items-center gap-1.5">
            <div className="rounded-lg bg-rose-50 p-1 dark:bg-rose-900/30">
              <TrendingDown className="text-rose-600" size={12} />
            </div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-text-muted sm:text-xs">Egresos</p>
          </div>
          <p className="text-sm font-bold text-rose-600 sm:text-xl md:text-2xl">
            ${stats.outcome.toLocaleString()}
          </p>
        </div>

        {/* Balance */}
        <div className="col-span-1 flex flex-col items-center justify-center gap-1 rounded-2xl border border-border bg-surface px-1 py-3 text-center shadow-sm sm:items-start sm:px-4 sm:text-left">
          <div className="flex items-center gap-1.5">
            <div className="rounded-lg bg-blue-50 p-1 dark:bg-blue-900/30">
              <Wallet className="text-blue-600" size={12} />
            </div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-text-muted sm:text-xs">Balance</p>
          </div>
          <p className={cn('text-sm font-bold sm:text-xl md:text-2xl', stats.balance >= 0 ? 'text-blue-600' : 'text-rose-600')}>
            ${stats.balance.toLocaleString()}
          </p>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="rounded-2xl border border-border bg-surface p-4 shadow-sm">
        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" size={16} />
          <input
            type="text"
            placeholder="Buscar por orden, motivo o método..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-xl border border-border bg-muted py-2.5 pl-10 pr-4 text-sm text-text placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
          />
        </div>

        {/* Date filters */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {(Object.keys(dateFilterLabels) as Array<keyof typeof dateFilterLabels>).map((filter) => (
            <button
              key={filter}
              onClick={() => setDateFilter(filter)}
              className={cn(
                'flex-shrink-0 rounded-xl px-4 py-1.5 text-xs font-semibold transition-all duration-200',
                dateFilter === filter
                  ? 'bg-primary text-primary-fg shadow-md'
                  : 'bg-muted text-text-muted hover:bg-primary/10 hover:text-primary'
              )}
            >
              {dateFilterLabels[filter]}
            </button>
          ))}
        </div>
      </div>

      {/* ── List ── */}
      {filteredMovements.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-border bg-surface py-16 text-center shadow-sm">
          <div className="rounded-2xl bg-muted p-4">
            <Filter size={28} className="text-text-muted" />
          </div>
          <div>
            <p className="font-semibold text-text">Sin movimientos</p>
            <p className="mt-0.5 text-sm text-text-muted">Prueba con otros filtros</p>
          </div>
        </div>
      ) : (
        <>
          {/* Desktop table (hidden on mobile) */}
          <div className="hidden md:block overflow-hidden rounded-2xl border border-border bg-surface shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-text-muted">Tipo</th>
                    <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-text-muted">Fecha</th>
                    <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-text-muted">Detalles</th>
                    <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-text-muted">Método</th>
                    <th className="px-5 py-3.5 text-right text-xs font-semibold uppercase tracking-wider text-text-muted">Monto</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filteredMovements.map((m) => {
                    const config = typeConfig[m.type] || typeConfig['sale'];
                    const Icon = config.icon;
                    const MethodIcon = methodIcons[m.method?.toUpperCase() || ''] || Banknote;
                    return (
                      <tr key={m.id} className="transition-colors hover:bg-muted/40">
                        <td className="px-5 py-4 whitespace-nowrap">
                          <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold', config.bg, config.darkBg, config.color)}>
                            <Icon size={13} />
                            {config.label}
                          </span>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-sm text-text-muted">
                          <div className="flex items-center gap-1.5">
                            <Calendar size={13} />
                            {new Date(m.createdAt).toLocaleString()}
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm text-text">
                          <div className="flex flex-col">
                            <span className="font-medium">{m.orderId ? `Orden #${m.orderId}` : (m.reason || 'Sin descripción')}</span>
                            {m.orderId && <span className="text-xs text-text-muted">Venta registrada</span>}
                          </div>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2 text-sm text-text-muted">
                            <div className="rounded-lg bg-muted p-1.5"><MethodIcon size={13} /></div>
                            <span className="capitalize">{m.method?.toLowerCase() || 'Efectivo'}</span>
                          </div>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap text-right">
                          <span className={cn('text-sm font-bold', m.type === 'sale' ? 'text-emerald-600' : 'text-rose-600')}>
                            {m.type === 'sale' ? '+' : '-'}${m.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile cards (hidden on desktop) */}
          <div className="space-y-3 md:hidden">
            {filteredMovements.map((m) => {
              const config = typeConfig[m.type] || typeConfig['sale'];
              const Icon = config.icon;
              const MethodIcon = methodIcons[m.method?.toUpperCase() || ''] || Banknote;
              const isIncome = m.type === 'sale';

              return (
                <div key={m.id} className="overflow-hidden rounded-2xl border border-border bg-surface shadow-sm transition-all duration-200 active:scale-[0.99]">
                  {/* Card top bar */}
                  <div className={cn('h-1 w-full', isIncome ? 'bg-emerald-500' : (m.type === 'expense' ? 'bg-amber-500' : 'bg-rose-500'))} />
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      {/* Left: icon + info */}
                      <div className="flex items-start gap-3">
                        <div className={cn('flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl', config.bg, config.darkBg)}>
                          <Icon className={config.color} size={20} />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-text">
                            {m.orderId ? `Orden #${m.orderId}` : (m.reason || 'Sin descripción')}
                          </p>
                          <div className="mt-0.5 flex items-center gap-1.5 text-xs text-text-muted">
                            <Calendar size={11} />
                            {new Date(m.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>

                      {/* Right: amount */}
                      <p className={cn('flex-shrink-0 text-base font-bold tabular-nums', isIncome ? 'text-emerald-600' : 'text-rose-600')}>
                        {isIncome ? '+' : '-'}${m.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </p>
                    </div>

                    {/* Footer */}
                    <div className="mt-3 flex items-center gap-2 border-t border-border pt-3">
                      <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold', config.bg, config.darkBg, config.color)}>
                        <Icon size={10} />
                        {config.label}
                      </span>
                      <div className="flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-text-muted">
                        <MethodIcon size={10} />
                        <span className="capitalize">{m.method?.toLowerCase() || 'efectivo'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
